// Typed fetch wrapper з JWT і автоматичним refresh.
const API_BASE = import.meta.env.VITE_API_BASE ?? "";

const TOKEN_KEY = "medsys_token";
const REFRESH_KEY = "medsys_refresh";

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}
export function setToken(t: string) {
  localStorage.setItem(TOKEN_KEY, t);
}
export function getRefresh(): string | null {
  return localStorage.getItem(REFRESH_KEY);
}
export function setRefresh(t: string) {
  localStorage.setItem(REFRESH_KEY, t);
}
export function clearTokens() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_KEY);
}

export class ApiError extends Error {
  constructor(public status: number, public detail: string, public body: unknown) {
    super(`API ${status}: ${detail}`);
  }
}

async function request<T>(
  path: string,
  init: RequestInit = {},
  retried = false
): Promise<T> {
  const token = getToken();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(init.headers as Record<string, string>),
  };
  if (token) headers["Authorization"] = `Bearer ${token}`;

  const url = path.startsWith("http") ? path : `${API_BASE}${path}`;
  const r = await fetch(url, { ...init, headers });

  if (r.status === 401 && !retried) {
    const ok = await tryRefresh();
    if (ok) return request<T>(path, init, true);
  }

  if (!r.ok) {
    let body: any = null;
    try {
      body = await r.json();
    } catch {}
    throw new ApiError(r.status, body?.detail ?? r.statusText, body);
  }

  if (r.status === 204) return null as unknown as T;
  return r.json() as Promise<T>;
}

async function tryRefresh(): Promise<boolean> {
  const refresh = getRefresh();
  if (!refresh) return false;
  try {
    const r = await fetch(`${API_BASE}/api/v1/auth/refresh`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refresh_token: refresh }),
    });
    if (!r.ok) return false;
    const j = await r.json();
    setToken(j.access_token);
    setRefresh(j.refresh_token);
    return true;
  } catch {
    return false;
  }
}

export const api = {
  get: <T>(p: string) => request<T>(p),
  post: <T>(p: string, body?: unknown) =>
    request<T>(p, { method: "POST", body: body !== undefined ? JSON.stringify(body) : undefined }),
  put: <T>(p: string, body?: unknown) =>
    request<T>(p, { method: "PUT", body: body !== undefined ? JSON.stringify(body) : undefined }),
  patch: <T>(p: string, body?: unknown) =>
    request<T>(p, { method: "PATCH", body: body !== undefined ? JSON.stringify(body) : undefined }),
  delete: <T>(p: string) => request<T>(p, { method: "DELETE" }),
  download: (p: string, fallbackName?: string) => downloadFile(p, fallbackName),
};

async function downloadFile(path: string, fallbackName?: string, retried = false): Promise<void> {
  const token = getToken();
  const headers: Record<string, string> = {};
  if (token) headers["Authorization"] = `Bearer ${token}`;
  const url = path.startsWith("http") ? path : `${API_BASE}${path}`;
  const r = await fetch(url, { headers });

  if (r.status === 401 && !retried) {
    const ok = await tryRefresh();
    if (ok) return downloadFile(path, fallbackName, true);
  }

  if (!r.ok) {
    let body: any = null;
    try { body = await r.json(); } catch {}
    throw new ApiError(r.status, body?.detail ?? r.statusText, body);
  }

  const cd = r.headers.get("Content-Disposition") || "";
  const m = cd.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
  const filename = (m ? decodeURIComponent(m[1]) : fallbackName) || "download";

  const blob = await r.blob();
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objectUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
}

// ────── Domain types ──────
export interface CurrentUser {
  user_id: string;
  email: string;
  full_name: string;
  role: "super_admin" | "admin" | "analyst" | "viewer" | "donor";
  is_active: boolean;
  last_login_at: string | null;
}

export interface MethodologySection {
  anchor: string;
  title: string;
  level: number;
  body_md: string;
  parent_anchor: string | null;
}

export interface HelpTopic {
  anchor: string;
  title: string;
  body_md: string;
  related_anchors: string[];
  seeded_from: string | null;
  updated_at: string;
}

export interface FacilityListItem {
  edrpou: string;
  name: string;
  care_type: string | null;
  property_type: string | null;
  status: string;
  network_role: string | null;
  area: string | null;
  settlement_name: string | null;
  lat: number | null;
  lng: number | null;
}
export interface FacilityListResponse {
  items: FacilityListItem[];
  count: number;
  total: number;
  limit: number;
  offset: number;
}
export interface FacilityDetail extends FacilityListItem {
  legal_entity_id: string | null;
  email: string | null;
  website: string | null;
  phone: string | null;
  owner_name: string | null;
  gromada_name: string | null;
  settlement_type: string | null;
  address: string | null;
  geocoded_via: string | null;
  geocoded_at: string | null;
  divisions: any[] | null;
  first_seen_at: string;
  last_synced_at: string | null;
}

export interface AuditLogItem {
  log_id: string;
  user_id: string | null;
  action: string;
  object_type: string | null;
  object_id: string | null;
  ip: string | null;
  user_agent: string | null;
  payload: any;
  created_at: string;
}

export interface SourceUptime {
  source: string;
  total_runs: number;
  success: number;
  partial: number;
  failed: number;
  uptime_pct: number;
  last_run_at: string | null;
  last_status: string | null;
  avg_duration_seconds: number | null;
}
export interface UptimeResponse {
  period_days: number;
  sources: SourceUptime[];
}

export interface DataQualityFinding {
  finding_id: string;
  source: string;
  severity: "critical" | "high" | "medium" | "low";
  field_name: string | null;
  expected: string | null;
  actual: string | null;
  action_taken: string | null;
}
export interface DataQualityRun {
  run_id: string;
  started_at: string;
  finished_at: string | null;
  status: string;
  summary: any;
  triggered_by: string;
  findings: DataQualityFinding[];
}
export interface DataQualityResponse {
  last_run: DataQualityRun | null;
  history_count: number;
}

export interface UserItem {
  user_id: string;
  email: string;
  full_name: string;
  role: string;
  is_active: boolean;
  created_at: string;
  last_login_at: string | null;
}

export interface PackageItem {
  package_id: number;
  package_name: string;
  group_name: string | null;
  group_type: string | null;
  medical_program: string | null;
  is_in_focus: boolean;
  is_pediatric: boolean;
  is_acute: boolean;
  is_rehab: boolean;
}

export interface FacilityFeature {
  type: "Feature";
  geometry: { type: "Point"; coordinates: [number, number] };
  properties: {
    edrpou: string;
    name: string;
    care_type: string | null;
    status: string;
    network_role: string | null;
    area: string | null;
    settlement_name: string | null;
    composite: number | null;
    activity: number | null;
    efficiency: number | null;
    care_routing: number | null;
    financial_health: number | null;
    dynamics: number | null;
    data_sufficient: boolean;
    coverage_packages: number;
  };
}
export interface FacilitiesGeoJSON {
  type: "FeatureCollection";
  features: FacilityFeature[];
  _count: number;
}

export interface FacilityTimeseries {
  facility: { edrpou: string; name: string; area: string | null; care_type: string | null };
  available_range: { earliest: string | null; latest: string | null };
  applied_filter: { date_from: string | null; date_to: string | null };
  services_monthly: { date: string; services: number; episodes: number }[];
  services_by_package: ({ date: string } & Record<string, number | string>)[];
  package_names: Record<string, string>;
  payments_monthly: { date: string; amount_uah: number }[];
  referrals_monthly: { date: string; created: number; completed: number; recalled: number; error: number }[];
  finance_quarterly: { period: string; revenue_uah: number | null; expense_uah: number | null; assets_uah: number | null }[];
}

export interface ScoreHistoryEntry {
  snapshot_date: string;
  score_activity: number | null;
  score_efficiency: number | null;
  score_care_routing: number | null;
  score_financial_health: number | null;
  score_dynamics: number | null;
  score_composite_default: number | null;
  peer_group_key: string | null;
  data_sufficient: boolean;
  coverage_packages: number;
  is_current: boolean;
}

export interface ScoreHistory {
  edrpou: string;
  history: ScoreHistoryEntry[];
}

export interface SyncJobInfo {
  source: string;
  name: string;
  schedule: string;
  last_run: {
    status: string;
    started_at: string | null;
    finished_at: string | null;
    duration_seconds: number | null;
    rows_inserted: number;
    rows_updated: number;
    rows_skipped: number;
    error_message: string | null;
    triggered_by: string;
  } | null;
  queue: {
    status: "pending" | "running" | "completed" | "failed";
    requested_at: string | null;
    picked_up_at: string | null;
    finished_at: string | null;
    error_message: string | null;
  } | null;
}

export interface SyncChunkInfo {
  log_id: string;
  source: string;
  period_covered: string;
  status: "success" | "failed" | "empty" | "partial";
  started_at: string | null;
  finished_at: string | null;
  duration_seconds: number | null;
  rows_inserted: number | null;
  rows_updated: number | null;
  rows_skipped: number | null;
  error_message: string | null;
  triggered_by: string | null;
}

export interface CompositeWeights {
  weights: {
    activity: number;
    efficiency: number;
    care_routing: number;
    financial_health: number;
    dynamics: number;
  };
  sum: number;
  is_default: boolean;
  default: CompositeWeights["weights"];
}

export type GroupPurpose = "control_group" | "peer_cohort" | "intervention_target" | "custom";

export interface HospitalGroup {
  group_id: string;
  name: string;
  purpose: GroupPurpose;
  description: string | null;
  color: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  is_archived: boolean;
  member_count: number;
}

export interface GroupMember {
  edrpou: string;
  name: string;
  area: string | null;
  care_type: string | null;
  score_composite: number | null;
  added_at: string;
}

export interface DashboardData {
  facilities: {
    total: number; active: number; new: number; relocated: number; closed: number;
    geocoded: number; geocoded_pct: number;
  };
  scores: {
    total_scored: number;
    data_sufficient: number;
    avg_composite: number | null;
    avg_activity: number | null;
    avg_efficiency: number | null;
    avg_dynamics: number | null;
    distribution: {
      leaders_75plus: number;
      solid_50_74: number;
      weak_25_49: number;
      critical_below_25: number;
    };
  };
  by_area: { area: string; facilities: number; avg_composite: number | null }[];
  payments: {
    facilities_with_pay: number;
    total_pay_billion_uah: number;
    avg_payment_uah: number;
    payment_records: number;
  };
  services: {
    facilities_with_services: number;
    total_services: number;
    total_episodes: number;
    earliest_month: string | null;
    latest_month: string | null;
  };
  interventions: {
    total: number; active: number; archived: number; total_budget_uah: number;
  };
  last_sync: {
    source: string; status: string; finished_at: string | null;
    rows_inserted: number; rows_updated: number;
  }[];
}

export interface PresetMeta {
  code: string;
  name_uk: string;
  description: string;
  icon: string;
  color: string;
  rationale: string;
  is_placeholder: boolean;
}

export interface PresetFacilityItem {
  edrpou: string;
  name: string;
  area: string | null;
  care_type: string | null;
  network_role: string | null;
  status: string;
  lat: number | null;
  lng: number | null;
  settlement_name: string | null;
  score_composite: number | null;
  score_activity: number | null;
  score_efficiency: number | null;
  score_care_routing: number | null;
  score_financial_health: number | null;
  score_dynamics: number | null;
  data_sufficient: boolean;
  coverage_packages: number;
}

export interface PresetAreaItem {
  area: string;
  facility_count: number;
  avg_composite: number | null;
}

export interface PresetFacilitiesResponse {
  preset: PresetMeta;
  kind: "facility_level" | "area_level";
  items: PresetFacilityItem[] | PresetAreaItem[];
  total: number;
  limit?: number;
  offset?: number;
}

export interface ScoreIndexExplain {
  value: number;
  method: string;
  formula: string;
  data_source: string;
  inputs: Record<string, any>;
  interpretation: string;
  is_placeholder: boolean;
}
export interface ScoresExplain {
  edrpou: string;
  name: string;
  peer_group: {
    key: string;
    area: string | null;
    care_type: string | null;
    size: number;
    members_with_services_data: number;
  };
  indices: {
    activity: ScoreIndexExplain;
    efficiency: ScoreIndexExplain;
    care_routing: ScoreIndexExplain;
    financial_health: ScoreIndexExplain;
    dynamics: ScoreIndexExplain;
  };
  composite: {
    value: number;
    weights: Record<string, number>;
    contributions: { index: string; score: number; weight: number; contribution: number }[];
    formula: string;
  };
}

export type InterventionType = "equipment" | "training" | "grant" | "mentoring" | "complex";

export interface Intervention {
  intervention_id: string;
  edrpou: string;
  type: InterventionType;
  title: string;
  description: string | null;
  date_start: string;
  date_end: string | null;
  expected_lag_months: number | null;
  parameters_affected: any | null;
  parameters_to_watch: any | null;
  target_packages: number[] | null;
  budget_uah: number | null;
  attachments: any | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  is_archived: boolean;
}

export interface InterventionCreate {
  edrpou: string;
  type: InterventionType;
  title: string;
  description?: string | null;
  date_start: string;
  date_end?: string | null;
  expected_lag_months?: number | null;
  target_packages?: number[] | null;
  budget_uah?: number | null;
}

export interface FacilityScores {
  edrpou: string;
  snapshot_date: string | null;
  peer_group_key: string | null;
  score_activity: number | null;
  score_efficiency: number | null;
  score_care_routing: number | null;
  score_financial_health: number | null;
  score_dynamics: number | null;
  score_composite_default: number | null;
  sub_scores: Record<string, { activity: number; total: number }> | null;
  data_sufficient: boolean;
  coverage_packages: number;
  note: string;
}
